import React, { useEffect, useState, useCallback } from 'react';
import { clientApi } from '../services/api';
import { readCache, writeCache, clearCache } from '../lib/declareCache';
import LenderCard from '../components/LenderCard';
import MailtoFallback from '../components/MailtoFallback';
import CompletionScreen from '../components/CompletionScreen';
import ErrorScreen from '../components/ErrorScreen';
import BrandHeader from '../components/BrandHeader';
import LegalFooter from '../components/LegalFooter';

// HashRouter puts query params after the `#/declare?…` fragment, so
// `window.location.search` is empty for the magic-link URL. Pull the params
// from the slice of the hash that follows the first `?`.
function useMagicLinkAuth() {
  const [auth] = useState(() => {
    const hash = window.location.hash || '';
    const qIdx = hash.indexOf('?');
    const qs = qIdx >= 0 ? hash.slice(qIdx + 1) : '';
    const params = new URLSearchParams(qs);
    const id = params.get('id');
    const token = params.get('t');
    return id && token ? { id, token } : null;
  });
  return auth;
}

export default function Declare() {
  const auth = useMagicLinkAuth();
  const cacheKey = auth ? `${auth.id}:${auth.token}` : null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [fallbackOpen, setFallbackOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    if (!auth) {
      setError({ kind: 'invalid_link' });
      setLoading(false);
      return;
    }
    setError(null);

    const cached = await readCache(cacheKey);
    if (cached) {
      setData((prev) => prev || cached);
      const firstPending = (cached.lenders || []).findIndex((l) => !['sent_by_client', 'bcc_received'].includes(l.status));
      setActiveIdx(firstPending === -1 ? (cached.lenders || []).length : firstPending);
      setLoading(false);
    } else {
      setLoading(true);
    }

    try {
      const res = await clientApi.init(auth);
      setData(res);
      const firstPending = (res.lenders || []).findIndex((l) => !['sent_by_client', 'bcc_received'].includes(l.status));
      setActiveIdx(firstPending === -1 ? (res.lenders || []).length : firstPending);
      writeCache(cacheKey, res);
    } catch (err) {
      if (err.status === 410) setError({ kind: 'expired' });
      else if (err.status === 401 || err.status === 404) { setError({ kind: 'invalid_link' }); clearCache(cacheKey); }
      else if (err.status === 503) { if (!cached) setError({ kind: 'upstream' }); }
      else if (!cached) setError({ kind: 'unknown', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [auth, cacheKey]);

  useEffect(() => { load(); }, [load]);

  const showToast = (text) => {
    setToast(text);
    setTimeout(() => setToast(null), 1800);
  };

  const handleMarkSent = async (lenderName) => {
    if (!data) return;
    const updated = data.lenders.map((l) => l.lenderName === lenderName ? { ...l, status: 'sent_by_client', clientMarkedSentAt: new Date().toISOString() } : l);
    setData({ ...data, lenders: updated, completedCount: updated.filter((l) => ['sent_by_client', 'bcc_received'].includes(l.status)).length });
    setFallbackOpen(false);
    const nextIdx = updated.findIndex((l, i) => i > activeIdx && !['sent_by_client', 'bcc_received'].includes(l.status));
    setActiveIdx(nextIdx === -1 ? updated.length : nextIdx);
    try {
      await clientApi.markSent(auth, lenderName);
    } catch (err) {
      showToast("Couldn't save. We'll retry.");
      queueRetry(() => clientApi.markSent(auth, lenderName));
    }
  };

  const handleMarkUnsent = async (lenderName) => {
    if (!data) return;
    const updated = data.lenders.map((l) => l.lenderName === lenderName ? { ...l, status: 'pending', clientMarkedSentAt: null } : l);
    const idx = updated.findIndex((l) => l.lenderName === lenderName);
    setData({ ...data, lenders: updated, completedCount: updated.filter((l) => ['sent_by_client', 'bcc_received'].includes(l.status)).length });
    if (idx >= 0) setActiveIdx(idx);
    try { await clientApi.markUnsent(auth, lenderName); } catch {}
  };

  const handleReportProblem = async (lenderName, reason) => {
    try { await clientApi.reportProblem(auth, lenderName, reason); } catch {}
    setFallbackOpen(true);
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center' }}>
          <span className="spinner" aria-label="Loading" />
          <p className="muted" style={{ marginTop: 12 }}>Loading your details…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorScreen kind={error.kind} supportEmail={data?.supportEmail} onRetry={load} />;
  }

  if (!data) return null;

  if (data.status === 'no_lenders') {
    return (
      <div className="container">
        <BrandHeader branding={data.branding} />
        <div className="card stack">
          <h1 className="h1">No action is needed right now</h1>
          <p className="muted">We've logged this and a Fair team member will be in touch if anything is required.</p>
        </div>
        <LegalFooter disclosure={data.legalDisclosure} />
      </div>
    );
  }

  const allDone = (data.lenders || []).every((l) => ['sent_by_client', 'bcc_received'].includes(l.status));
  if (allDone) {
    return <CompletionScreen data={data} />;
  }

  const activeLender = data.lenders[activeIdx];

  return (
    <div className="container">
      <BrandHeader branding={data.branding} />

      <div className="card">
        <div className="row space">
          <div>
            <h1 className="h1">Hi {data.firstName || 'there'},</h1>
            <p className="muted">{data.landingGreetingCopy || "To help us continue progressing your claim, your lender has asked you to confirm your authority directly from your own email account."}</p>
          </div>
        </div>
        <div style={{ marginTop: 12 }} className="stack">
          <div className="row space">
            <div className="small muted">Lender {Math.min(activeIdx + 1, data.totalCount)} of {data.totalCount}</div>
            <div className="small muted">{data.completedCount} of {data.totalCount} Confirmations Sent</div>
          </div>
          <div className="progress"><div className="bar" style={{ width: `${(data.completedCount / data.totalCount) * 100}%` }} /></div>
        </div>
      </div>

      {fallbackOpen && activeLender ? (
        <MailtoFallback
          lender={activeLender}
          firstName={data.firstName}
          bcc={data.bccRoutingAddress}
          templates={data.templates}
          onMarkSent={() => handleMarkSent(activeLender.lenderName)}
          onBack={() => setFallbackOpen(false)}
          showToast={showToast}
        />
      ) : activeLender ? (
        <LenderCard
          lender={activeLender}
          firstName={data.firstName}
          bcc={data.bccRoutingAddress}
          templates={data.templates}
          onMarkSent={() => handleMarkSent(activeLender.lenderName)}
          onMarkUnsent={() => handleMarkUnsent(activeLender.lenderName)}
          onDidntWork={() => handleReportProblem(activeLender.lenderName, 'mailto_did_not_open')}
        />
      ) : null}

      <div className="card small muted">
        Need help? Contact us at {data.supportEmail ? (<a className="link-action" href={`mailto:${data.supportEmail}`}>{data.supportEmail}</a>) : 'the number on your notification email'}.
      </div>

      <LegalFooter disclosure={data.legalDisclosure} />

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function queueRetry(fn, attempts = 5) {
  let n = 0;
  const id = setInterval(async () => {
    n += 1;
    try { await fn(); clearInterval(id); } catch {
      if (n >= attempts) clearInterval(id);
    }
  }, 30000);
}
